<template>
  <VDialog v-model="modelShow" max-width="980px" :z-index="9999" attach="body">
    <VCard class="flex flex-col gap-4 px-4 pb-4 sm:px-8 sm:pb-8">
      <VCardTitle class="sticky z-10 top-0 -mx-8 bg-white">
        <div class="flex justify-between items-center">
          <h3 class="text-h3 p-6">
            <p>{{ getFormattedPatientName(caze.patient) }}</p>
          </h3>
          <VBtn
            icon="close"
            variant="plain"
            class="button-small mr-2"
            size="small"
            :ripple="false"
            data-testid="btn-close"
            @click="closeDialog"
          />
        </div>
        <DividerLine class="-mx-8" />
      </VCardTitle>

      <div class="card">
        <h4 class="text-h4 mb-4 font-bold">{{ t('Vital Signs') }}</h4>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <ResultItem
            v-for="(vital, index) in vitalSignsItems"
            :key="index"
            :label="vital.label"
            :value="vital.value"
            :timestamp="vital.timestamp"
          />
        </div>
      </div>

      <div class="card flex flex-col">
        <h4 class="text-h4 mb-4 font-bold">{{ t('Organ Support') }}</h4>
        <div class="flex flex-wrap gap-4">
          <div
            v-for="support in organSupportItems"
            :key="support.label"
            class="bg-[#F3F4F7] flex flex-1 basis-1/6 flex-col items-center justify-center rounded-md p-2 min-w-[160px]"
          >
            <span class="text-caption text-[#206683]">{{ support.label }}</span>
            <span class="text-subtitle-2 font-bold text-[#17485D]">
              <VIcon
                v-if="support.value !== null"
                size="16"
                :icon="support.value ? 'check' : 'remove'"
                :class="support.value ? 'text-success' : 'text-[#17485D]'"
              />
              <span v-else>-</span>
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="
          (isEdsEnabled && hasAcceptedEdsLimitation) || (isAkiEnabled && hasAcceptedAkiLimitation)
        "
        class="card"
      >
        <h4 class="text-h4 mb-4 font-bold">{{ t('Service Scores') }}</h4>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <ResultItem
            v-if="isEdsEnabled && hasAcceptedEdsLimitation"
            :label="t('EDS Score')"
            :value="formatValue(edsScore?.scoreValue, NumberFormat.INTEGER)"
            :timestamp="edsScore?.scoreTimestamp ? formatTimestamp(edsScore.scoreTimestamp) : '–'"
          />
          <AkiResultItem v-if="isAkiEnabled && hasAcceptedAkiLimitation" :aki-score="akiScore" />
        </div>
      </div>

      <div class="card">
        <h4 class="text-h4 mb-4 font-bold">{{ t('Lab Values') }}</h4>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <ResultItem
            v-for="(vital, index) in laboratoryItems"
            :key="index"
            :label="vital.label"
            :value="vital.value"
            :timestamp="vital.timestamp"
          />
        </div>
      </div>

      <DividerLine class="-mx-8" />

      <div class="mt-2 flex flex-wrap justify-end gap-4 flex-col sm:flex-row">
        <VBtn
          variant="outlined"
          color="primary"
          data-testid="btn-cancel"
          class="button-large min-w-[150px]"
          size="large"
          :text="t('Close')"
          @click="closeDialog"
        />
        <VBtn
          variant="flat"
          color="primary"
          class="button-large min-w-[150px]"
          size="large"
          :text="t('Patient Details')"
          @click="handleDisplayValuePatientDetails"
          append-icon="chevron_right"
        />
      </div>
    </VCard>
  </VDialog>
</template>

<script setup lang="ts">
import type {
  CaseDTO,
  ClinicalScoreResultDto,
  ErrorResponseDto,
  OrganSupportDTO,
  PredictionResultDTO,
  VitalSignItemDTO
} from '@/generated'
import { computed, toRef } from 'vue'
import { VitalSignsKeys } from '../../models/VitalSignsKeys'
import {
  findLatestTimeValueByKey,
  findLatestValueByKey,
  formatTimestamp
} from './VitalInfoTable.utils'
import { useI18n } from 'vue-i18n'
import ResultItem from '@/components/patient/components/atoms/ResultItem.vue'
import AkiResultItem from '@/components/patient/components/atoms/AkiResultItem.vue'
import { useRouter } from 'vue-router'
import DividerLine from '@/atoms/DividerLine.vue'
import { usePatientVitals } from '../../composables/usePatientVitals'
import useParameterLabel from '@/composables/useParameterLabel'
import { Parameter } from '@/enums/Parameter'
import { routeNames } from '@/router/routeNames'
import { useFormatValue } from '@/composables/useFormatValue'
import { NumberFormat } from '@/i18n/i18n'
import useFormattedPatientDetails from '@/composables/useFormattedPatientDetails'
import { useEdsPlugin } from '@/composables/useEdsPlugin'
import { storeToRefs } from 'pinia'
import { usePluginsStore } from '@/stores/plugins'
import { useAkiPlugin } from '@/composables/useAkiPlugin'

interface Props {
  show: boolean
  caze: CaseDTO
  vitalSigns: VitalSignItemDTO[]
  laboratory: VitalSignItemDTO[]
  organSupport: OrganSupportDTO
  shareCode?: string
  edsScore?: ClinicalScoreResultDto
  akiScore?: PredictionResultDTO | ErrorResponseDto
}

const props = defineProps<Props>()

const modelShow = defineModel<boolean>('show', { default: false })

const { t } = useI18n()
const { formatValue } = useFormatValue()
const { getFormattedPatientName } = useFormattedPatientDetails()
const router = useRouter()

const { isEdsEnabled, isAkiEnabled } = storeToRefs(usePluginsStore())
const { hasAcceptedEdsLimitation } = useEdsPlugin()
const { hasAcceptedAkiLimitation } = useAkiPlugin()

function closeDialog() {
  modelShow.value = false
}

const reactiveVitalSigns = toRef(props.vitalSigns)
const { INV_BP, NON_INV_BP, HEART_RATE, SPO2, RR, TEMP } = usePatientVitals(reactiveVitalSigns)
const { getLabelWithDefaultUnit, getParameterLabel } = useParameterLabel()

const organSupportItems = computed(() => [
  { label: t('Vasopressors'), value: props.organSupport.vasopressors },
  { label: t('Ventilation'), value: props.organSupport.ventilation },
  { label: t('Dialysis'), value: props.organSupport.dialysis },
  { label: t('ECMO'), value: props.organSupport.ecmo },
  { label: t('Impella'), value: props.organSupport.impella }
])

const vitalSignsItems = computed(() => [
  {
    label: getLabelWithDefaultUnit(Parameter.InvasiveBloodPressure),
    value: formatValue(INV_BP.value.displayValue),
    timestamp: findLatestTimeValueByKey(props.vitalSigns, VitalSignsKeys.INV_S_BP)
  },
  {
    label: getLabelWithDefaultUnit(Parameter.NonInvasiveBloodPressure),
    value: formatValue(NON_INV_BP.value.displayValue),
    timestamp: findLatestTimeValueByKey(props.vitalSigns, VitalSignsKeys.NON_INV_S_BP)
  },
  {
    label: getLabelWithDefaultUnit(Parameter.HeartRate),
    value: formatValue(HEART_RATE.value.displayValue, NumberFormat.INTEGER),
    timestamp: findLatestTimeValueByKey(props.vitalSigns, VitalSignsKeys.HEART_RATE)
  },
  {
    label: getLabelWithDefaultUnit(Parameter.OxygenSaturation),
    value: formatValue(SPO2.value.displayValue, NumberFormat.INTEGER),
    timestamp: findLatestTimeValueByKey(props.vitalSigns, VitalSignsKeys.SPO2)
  },
  {
    label: getLabelWithDefaultUnit(Parameter.RespiratoryRate),
    value: formatValue(RR.value.displayValue, NumberFormat.INTEGER),
    timestamp: findLatestTimeValueByKey(props.vitalSigns, VitalSignsKeys.RR)
  },
  {
    label: getLabelWithDefaultUnit(Parameter.Temperature),
    value: formatValue(TEMP.value.displayValue),
    timestamp: findLatestTimeValueByKey(props.vitalSigns, VitalSignsKeys.TEMP)
  }
])

const laboratoryItems = computed(() => [
  {
    label: getLabelWithDefaultUnit(Parameter.Lactate),
    value: formatValue(findLatestValueByKey(props.laboratory, VitalSignsKeys.LACTATE)?.value),
    timestamp: findLatestTimeValueByKey(props.laboratory, VitalSignsKeys.LACTATE)
  },
  {
    label: getParameterLabel(Parameter.PH),
    value: formatValue(findLatestValueByKey(props.laboratory, VitalSignsKeys.PH)?.value),
    timestamp: findLatestTimeValueByKey(props.laboratory, VitalSignsKeys.PH)
  },
  {
    label: getLabelWithDefaultUnit(Parameter.GFR),
    value: formatValue(findLatestValueByKey(props.laboratory, VitalSignsKeys.GFR)?.value),
    timestamp: findLatestTimeValueByKey(props.laboratory, VitalSignsKeys.GFR)
  }
])

function handleDisplayValuePatientDetails() {
  router.push({
    name: routeNames.PATIENT_DETAILS,
    params: { caseId: props.caze.id },
    query: { shareCode: props.shareCode }
  })
}
</script>

<style scoped>
.card {
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
</style>
